import { useState, useCallback, useRef, useEffect } from 'react';
import { speechService, SpeechToken } from '@/services/speechService';
import { logger } from '@/utils/logger';

// Lazy load the Speech SDK to avoid "Class extends value undefined" error
// This happens because the SDK has issues with ESM/Vite bundling when loaded at top level
type SpeechSDKType = typeof import('microsoft-cognitiveservices-speech-sdk');
let SpeechSDK: SpeechSDKType | null = null;

const loadSpeechSDK = async (): Promise<SpeechSDKType> => {
  if (SpeechSDK) return SpeechSDK;
  SpeechSDK = await import('microsoft-cognitiveservices-speech-sdk');
  return SpeechSDK;
};

export interface UseSpeechToTextOptions {
  language?: string;
  continuous?: boolean;
}

export interface UseSpeechToTextReturn {
  transcript: string;
  interimTranscript: string;
  isListening: boolean;
  isConnecting: boolean;
  error: string | null;
  startListening: () => Promise<void>;
  stopListening: () => void;
  resetTranscript: () => void;
  setLanguage: (language: string) => void;
  currentLanguage: string;
}

const DEFAULT_LANGUAGE = 'en-US';

export function useSpeechToText(options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const { language: initialLanguage = DEFAULT_LANGUAGE, continuous = true } = options;

  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // Use any type for recognizer since SDK is loaded dynamically
  const recognizerRef = useRef<any | null>(null);
  const tokenRef = useRef<SpeechToken | null>(null);
  const tokenExpiryRef = useRef<Date | null>(null);
  const sdkRef = useRef<SpeechSDKType | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.close();
        recognizerRef.current = null;
      }
    };
  }, []);

  // Check if token is still valid (with 1 minute buffer)
  const isTokenValid = useCallback(() => {
    if (!tokenRef.current || !tokenExpiryRef.current) return false;
    const buffer = 60 * 1000; // 1 minute
    return new Date().getTime() < tokenExpiryRef.current.getTime() - buffer;
  }, []);

  // Get or refresh token
  const getToken = useCallback(async (): Promise<SpeechToken> => {
    if (isTokenValid() && tokenRef.current) {
      return tokenRef.current;
    }

    const token = await speechService.getToken();
    tokenRef.current = token;
    tokenExpiryRef.current = new Date(token.expires_at);
    return token;
  }, [isTokenValid]);

  // Create speech config
  const createSpeechConfig = useCallback(
    async (token: SpeechToken, sdk: SpeechSDKType) => {
      const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
        token.token,
        token.region
      );
      speechConfig.speechRecognitionLanguage = currentLanguage;

      // Enable detailed output for better interim results
      speechConfig.outputFormat = sdk.OutputFormat.Detailed;

      return speechConfig;
    },
    [currentLanguage]
  );

  // Start listening
  const startListening = useCallback(async () => {
    if (isListening || isConnecting) return;

    setError(null);
    setIsConnecting(true);

    try {
      // Lazy load the Speech SDK
      const sdk = await loadSpeechSDK();
      sdkRef.current = sdk;

      // Get auth token
      const token = await getToken();

      // Create speech config
      const speechConfig = await createSpeechConfig(token, sdk);

      // Create audio config from default microphone
      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

      // Create recognizer
      const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
      recognizerRef.current = recognizer;

      // Handle recognizing event (interim results)
      recognizer.recognizing = (_sender: any, event: any) => {
        if (event.result.reason === sdk.ResultReason.RecognizingSpeech) {
          setInterimTranscript(event.result.text);
        }
      };

      // Handle recognized event (final results)
      recognizer.recognized = (_sender: any, event: any) => {
        if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
          const text = event.result.text;
          if (text) {
            setTranscript((prev) => {
              // Add space between sentences if needed
              if (prev && !prev.endsWith(' ') && !prev.endsWith('\n')) {
                return prev + ' ' + text;
              }
              return prev + text;
            });
            setInterimTranscript('');
          }
        } else if (event.result.reason === sdk.ResultReason.NoMatch) {
          // No speech could be recognized
          setInterimTranscript('');
        }
      };

      // Handle session stopped
      recognizer.sessionStopped = () => {
        setIsListening(false);
        setInterimTranscript('');
      };

      // Handle canceled
      recognizer.canceled = (_sender: any, event: any) => {
        if (event.reason === sdk.CancellationReason.Error) {
          setError(`Error: ${event.errorDetails}`);
        }
        setIsListening(false);
        setInterimTranscript('');
      };

      // Start recognition
      if (continuous) {
        await new Promise<void>((resolve, reject) => {
          recognizer.startContinuousRecognitionAsync(
            () => resolve(),
            (err: string) => reject(new Error(err))
          );
        });
      } else {
        await new Promise<void>((resolve, reject) => {
          recognizer.recognizeOnceAsync(
            (result: any) => {
              if (sdkRef.current && result.reason === sdkRef.current.ResultReason.RecognizedSpeech) {
                setTranscript((prev) => prev + result.text);
              }
              resolve();
            },
            (err: string) => reject(new Error(err))
          );
        });
      }

      setIsListening(true);
      setIsConnecting(false);
    } catch (err) {
      logger.error('Speech recognition error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start speech recognition');
      setIsConnecting(false);
      setIsListening(false);
    }
  }, [isListening, isConnecting, continuous, getToken, createSpeechConfig]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognizerRef.current) {
      if (continuous) {
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            setIsListening(false);
            setInterimTranscript('');
          },
          (err: string) => {
            logger.error('Error stopping recognition:', err);
            setIsListening(false);
          }
        );
      } else {
        recognizerRef.current.close();
        recognizerRef.current = null;
        setIsListening(false);
      }
    }
    setInterimTranscript('');
  }, [continuous]);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  // Set language
  const setLanguage = useCallback(
    (language: string) => {
      // If currently listening, stop first
      if (isListening) {
        stopListening();
      }
      setCurrentLanguage(language);
    },
    [isListening, stopListening]
  );

  return {
    transcript,
    interimTranscript,
    isListening,
    isConnecting,
    error,
    startListening,
    stopListening,
    resetTranscript,
    setLanguage,
    currentLanguage,
  };
}

export default useSpeechToText;
